// PlayFirst Parent — Full Language Map view.
import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Heart, Loader2, RefreshCw } from 'lucide-react';
import { PFP } from './styles';
import { pickActiveChild } from './childSelection';
import ChildSwitcher from './ChildSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function ParentAppLanguageMap() {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [child, setChild] = useState(null);
  const [data, setData] = useState(null);
  const [learningProfile, setLearningProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regen, setRegen] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const loadForChild = useCallback(async (c) => {
    setData(null);
    setLearningProfile(null);
    try {
      const lm = await axios.get(`${API}/parent-app/children/${c.id}/language-map`, auth());
      setData(lm.data);
      try {
        const lp = await axios.get(`${API}/parent-app/children/${c.id}/learning-profile`, auth());
        setLearningProfile(lp.data);
      } catch (e) { /* not generated yet — no-op */ }
    } catch (e) {
      toast.error('Could not load Language Map');
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const list = await axios.get(`${API}/parent-app/children`, auth());
        if (!list.data?.length) {
          navigate('/parent-app/onboarding');
          return;
        }
        setChildren(list.data);
        const c = pickActiveChild(list.data);
        setChild(c);
        await loadForChild(c);
      } catch (e) {
        toast.error('Could not load Language Map');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchChild = async (newId) => {
    const next = children.find((c) => c.id === newId);
    if (!next || next.id === child?.id) return;
    setChild(next);
    setLoading(true);
    try {
      await loadForChild(next);
    } finally {
      setLoading(false);
    }
  };

  const regenerate = async () => {
    if (!child) return;
    setRegen(true);
    try {
      const lm = await axios.post(`${API}/parent-app/children/${child.id}/language-map`, {}, auth());
      setData(lm.data);
      toast.success('Language Map refreshed');
    } catch (e) {
      toast.error('Could not refresh');
    } finally {
      setRegen(false);
    }
  };

  if (loading) {
    return (
      <div className={PFP.page + ' flex items-center justify-center'}>
        <Loader2 className="w-6 h-6 animate-spin text-[#7C6F9C]" />
      </div>
    );
  }

  const lm = data?.map;
  if (!lm) {
    return (
      <div className={PFP.page + ' p-8'}>
        <p>No Language Map yet.</p>
      </div>
    );
  }

  return (
    <div className={PFP.page}>
      <header className="px-4 sm:px-8 py-4 max-w-3xl mx-auto flex items-center justify-between">
        <Link to="/parent-app/home" className="flex items-center gap-2 text-stone-600 hover:text-[#7C6F9C]">
          <ArrowLeft className="w-5 h-5" /> Back
        </Link>
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-[#7C6F9C] fill-[#7C6F9C]/20" />
          <span className="font-serif text-lg text-[#3a2f4f]">PlayFirst Parent</span>
        </div>
        <button
          onClick={regenerate}
          disabled={regen}
          className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-[#7C6F9C]"
          data-testid="regenerate-language-map-btn"
        >
          {regen ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {regen ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 md:py-10 space-y-5">
        {children.length > 1 && (
          <ChildSwitcher items={children} value={child?.id} onChange={switchChild} />
        )}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className={PFP.h2}>{child.name}'s Language Map</h1>
          <p className={`${PFP.sub} mt-1`}>A gentle, parent-friendly summary — no scores, no checkboxes.</p>
        </motion.div>

        <MapSection title="How your child communicates now">{lm.current_communication_summary}</MapSection>

        {learningProfile?.profile?.summary && (
          <section
            className={`${PFP.card} p-6 bg-gradient-to-br from-[#FBF4F9] to-white`}
            data-testid="learning-profile-section"
          >
            <div className="flex items-center gap-2 mb-2">
              <p className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium">
                What we're learning ✨
              </p>
            </div>
            <p className="text-stone-700 leading-relaxed mb-3">
              {learningProfile.profile.summary}
            </p>
            {learningProfile.profile.engagement_signal && (
              <p className="text-sm text-stone-600 mb-2">
                <span className="text-[#7C6F9C] font-medium">Engagement signal:</span>{' '}
                {learningProfile.profile.engagement_signal}
              </p>
            )}
            {(learningProfile.profile.emerging_words || []).length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-1.5">
                  Emerging words
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {learningProfile.profile.emerging_words.map((w) => (
                    <span
                      key={w}
                      className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100"
                    >
                      + {w}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {lm.strengths?.length > 0 && (
          <MapSection title="Strengths we can build on">
            <ul className="space-y-2">
              {lm.strengths.map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#7C6F9C] mt-1">•</span>
                  <span className="text-stone-700">{s}</span>
                </li>
              ))}
            </ul>
          </MapSection>
        )}

        {lm.helpful_supports?.length > 0 && (
          <MapSection title="What seems to help">
            <ul className="space-y-2">
              {lm.helpful_supports.map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#7C6F9C] mt-1">•</span>
                  <span className="text-stone-700">{s}</span>
                </li>
              ))}
            </ul>
          </MapSection>
        )}

        {lm.emerging_focus && (
          <MapSection title="Emerging focus">{lm.emerging_focus}</MapSection>
        )}

        {lm.glp_notes && (
          <MapSection title="GLP-informed support">{lm.glp_notes}</MapSection>
        )}

        {lm.next_best_support && (
          <MapSection title="Helpful next step">{lm.next_best_support}</MapSection>
        )}

        {lm.gentle_guidance && (
          <div className="p-5 rounded-3xl bg-[#FFF7EA] border border-amber-200">
            <p className="text-sm text-amber-900 leading-relaxed">{lm.gentle_guidance}</p>
          </div>
        )}

        <footer className="text-center text-xs text-stone-400 pt-4">
          PlayFirst Parent is not a diagnostic, screening or emergency tool.
        </footer>
      </main>
    </div>
  );
}

function MapSection({ title, children }) {
  return (
    <section className={`${PFP.card} p-6`}>
      <p className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium mb-2">{title}</p>
      <div className="text-stone-700 leading-relaxed">{children}</div>
    </section>
  );
}
